import React, { useMemo, useState, useEffect } from "react";
import { useMatchPath } from "@/lib/react-router";
import { StringParam, useQueryParams } from "use-query-params";
import { storage } from "@/utils/window";
import useAuthenticatedUser from "@/hooks/useAuthenticatedUser";
import useGatekeeper from "@/hooks/useGatekeeper";
import LayoutAlert from "@/ui-lib/components/LayoutAlert";
import Dropdown from "@/ui-lib/components/Dropdown";
import ParsedText from "@/ui-lib/components/ParsedText";
import copyText from "@/constants/copyText";
import { locationStateKeyedByPath } from "@/constants/paths";
import { useTheme } from "@emotion/react";
import Text from "@ternary/api-lib/ui-lib/components/Text";

const STORAGE_KEY = "multi_currency_alert_state";

export default function MultiCurrencyAlert() {
  const theme = useTheme();
  const authenticatedUser = useAuthenticatedUser();
  const gatekeeper = useGatekeeper();
  const currentPath = useMatchPath();
  const [queryParams] = useQueryParams({ tab: StringParam });
  const [isVisible, setIsVisible] = useState(true);

  const isInternalUser = authenticatedUser.email.endsWith("@ternary.app");

  const handleDismiss = (type: "page" | "forever") => {
    const alertState = JSON.parse(
      storage.getItem(STORAGE_KEY) ||
        JSON.stringify({ dismissedForever: false, dismissedPages: [] })
    );

    switch (type) {
      case "page":
        alertState.dismissedPages.push(currentPath);
        break;
      case "forever":
        alertState.dismissedForever = true;
        break;
    }

    storage.setItem(STORAGE_KEY, JSON.stringify(alertState));
    setIsVisible(false);
  };

  const options = useMemo(
    () => [
      {
        label: copyText.actionDismissPage,
        onClick: () => handleDismiss("page"),
      },
      {
        label: copyText.actionDismissForever,
        onClick: () => handleDismiss("forever"),
      },
    ],
    [handleDismiss]
  );

  useEffect(() => {
    const alertState = JSON.parse(
      storage.getItem(STORAGE_KEY) ||
        JSON.stringify({ dismissedForever: false, dismissedPages: [] })
    );

    if (
      alertState.dismissedForever ||
      alertState.dismissedPages.includes(currentPath) ||
      !gatekeeper.canAccessMultiCurrency
    ) {
      setIsVisible(false);
      return;
    }

    const locationState = locationStateKeyedByPath?.[currentPath];
    const multiCurrencyConfig = locationState?.enableMultiCurrency;

    if (typeof multiCurrencyConfig === "boolean") {
      setIsVisible(!multiCurrencyConfig);
      return;
    }

    if (multiCurrencyConfig?.partial) {
      setIsVisible(true);
      return;
    }

    if (multiCurrencyConfig?.enabledForTab) {
      setIsVisible(multiCurrencyConfig.enabledForTab !== queryParams.tab);
      return;
    }

    setIsVisible(false);
  }, [currentPath, queryParams.tab, gatekeeper.canAccessMultiCurrency]);

  if (!isVisible) {
    return null;
  }

  return (
    <LayoutAlert
      message={
        <ParsedText
          text={copyText.currencyAlertMessage}
          components={{ b: <b /> }}
        />
      }
      title={copyText.currencyAlertTitle}
      action={
        isInternalUser ? (
          <Dropdown options={options} placement="bottom-end">
            <Text color={theme.primary_color_background}>
              {copyText.actionDismiss}
            </Text>
          </Dropdown>
        ) : (
          copyText.actionDismiss
        )
      }
      onActionClick={isInternalUser ? undefined : () => handleDismiss("page")}
    />
  );
}
